import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Contestacao.css";
import { useAuth } from "../context/ApiKeyContext";
import { useHistory } from "react-router-dom";
import { useTranslation } from "../hooks/useTranslation";
import CustomizationSliders from "../components/CustomizationSliders";
import GenerationModeSelector from "../components/GenerationModeSelector";
import { GENERATION_MODES } from "../constants/generationModes";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Fullscreen, FullscreenExit, Info } from "@material-ui/icons";
import {
  CONTESTATION_SERVICE,
  DOCUMENT_CREATION_PRODUCT_CODES,
  readServiceCustomization,
  readContestationCellRulesAsObservations,
} from "../utils/serviceCustomizationApi";
import { pdfjs } from "react-pdf";
import { getApiBaseUrl } from "../config";
import {
  REPLICATE_CONTESTACAO_STORAGE_KEY,
  fetchContestacaoInputFileBlob,
  isReplicatableContestacaoProductCode,
} from "../utils/replicateContestacaoJob";

// Configurar worker do PDF.js usando a mesma configuração dos outros componentes
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// URL base da API - usa variável de ambiente ou fallback para produção
const API_BASE_URL = getApiBaseUrl();

const Contestacao = () => {
  const [formValid, setFormValid] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Obter a API Key do contexto
  const { token, cell, user, hasRole } = useAuth();
  const history = useHistory();
  const { t } = useTranslation("jobs");

  const [fileData, setFileData] = useState({
    inicialFile: null,
    subsidiosFiles: [],
  });

  // Estado para armazenar número de páginas dos arquivos
  const [filePages, setFilePages] = useState({});

  const [formData, setFormData] = useState({
    lawyerName: "",
    lawyerOAB: "",
    local: "",
    date: "",
    empresaDefendida: "",
    jobName: "",
    observacoesGerais: "",
  });

  // Estados para parâmetros de personalização
  const [useDefaultParams, setUseDefaultParams] = useState(false);
  const [customizationParams, setCustomizationParams] = useState({
    creativity: 2,
    tone: 2,
    scope_topics: 2,
    complexity: 2,
    foundation_depth: 2,
  });
  // Estado para controlar quais parâmetros foram sobrescritos individualmente
  const [overriddenParams, setOverriddenParams] = useState({
    creativity: false,
    tone: false,
    scope_topics: false,
    complexity: false,
    foundation_depth: false,
  });
  const [observations, setObservations] = useState({
    client: "",
    internal: "",
    subsidios: "",
  });

  // Estado para modo de geração
  const [generationMode, setGenerationMode] = useState("standard");

  // Estados para loading de parâmetros da célula
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [loadDefaultsError, setLoadDefaultsError] = useState(false);
  const [defaultsRetryCountdown, setDefaultsRetryCountdown] = useState(0);
  const cancelDefaultsRetryRef = useRef(false);
  const defaultsRetryTimerRef = useRef(null);

  // Estados para modal fullscreen de textarea
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenField, setFullscreenField] = useState(null); // 'client', 'internal', 'subsidios'
  const [fullscreenValue, setFullscreenValue] = useState("");
  const [replicateLoadError, setReplicateLoadError] = useState(null);

  // Garantir que observations sempre tenha valores válidos
  const safeObservations = {
    client: observations?.client || "",
    internal: observations?.internal || "",
    subsidios: observations?.subsidios || "",
  };

  // Função para verificar se o usuário tem permissão para editar parâmetros na criação
  const hasCustomizationPermission = () => {
    if (!user) return false;
    if (!user.permissions) return false;
    return hasRole(
      "DOCUMENT_CREATION",
      "edit_custom_specific",
      DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
    );
  };

  // Handlers estáveis para as observações usando useCallback
  const handleClientObservationChange = useCallback((e) => {
    try {
      console.log("handleClientObservationChange called", {
        e,
        target: e?.target,
      });

      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn("Event target is null in handleClientObservationChange", {
          e,
        });
        return;
      }

      const value = e.target.value;
      console.log("Setting client observation value:", value);

      setObservations((prev) => {
        console.log("Previous observations:", prev);
        const newObs = { ...prev, client: value };
        console.log("New observations:", newObs);
        return newObs;
      });
    } catch (error) {
      console.error("Erro em handleClientObservationChange:", error, {
        event: e,
        stack: error.stack,
      });
    }
  }, []);

  const handleInternalObservationChange = useCallback((e) => {
    try {
      console.log("handleInternalObservationChange called", {
        e,
        target: e?.target,
      });

      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn(
          "Event target is null in handleInternalObservationChange",
          { e },
        );
        return;
      }

      const value = e.target.value;
      console.log("Setting internal observation value:", value);

      setObservations((prev) => {
        console.log("Previous observations:", prev);
        const newObs = { ...prev, internal: value };
        console.log("New observations:", newObs);
        return newObs;
      });
    } catch (error) {
      console.error("Erro em handleInternalObservationChange:", error, {
        event: e,
        stack: error.stack,
      });
    }
  }, []);

  const handleSubsidiosObservationChange = useCallback((e) => {
    try {
      console.log("handleSubsidiosObservationChange called", {
        e,
        target: e?.target,
      });

      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn(
          "Event target is null in handleSubsidiosObservationChange",
          { e },
        );
        return;
      }

      const value = e.target.value;
      console.log("Setting subsidios observation value:", value);

      setObservations((prev) => {
        console.log("Previous observations:", prev);
        const newObs = { ...prev, subsidios: value };
        console.log("New observations:", newObs);
        return newObs;
      });
    } catch (error) {
      console.error("Erro em handleSubsidiosObservationChange:", error, {
        event: e,
        stack: error.stack,
      });
    }
  }, []);

  // Funções para expandir textarea em tela cheia
  const handleExpandTextarea = (field) => {
    setFullscreenField(field);
    setFullscreenValue(safeObservations[field] || "");
    setFullscreenOpen(true);
  };

  const handleCloseFullscreen = () => {
    // Salvar o valor quando fechar
    if (fullscreenField) {
      const newObservations = { ...observations };
      newObservations[fullscreenField] = fullscreenValue;
      setObservations(newObservations);
    }
    setFullscreenOpen(false);
    setFullscreenField(null);
    setFullscreenValue("");
  };

  const handleUseDefaultParamsChange = useCallback((e) => {
    try {
      console.log("handleUseDefaultParamsChange called", {
        e,
        target: e?.target,
      });

      // Verificação de segurança para evitar erro quando e.target é null
      if (!e || !e.target) {
        console.warn("Event target is null in handleUseDefaultParamsChange", {
          e,
        });
        return;
      }

      const checked = e.target.checked;
      console.log("Setting useDefaultParams:", checked);

      setUseDefaultParams(checked);
    } catch (error) {
      console.error("Erro em handleUseDefaultParamsChange:", error, {
        event: e,
        stack: error.stack,
      });
    }
  }, []);

  // Função interna que executa a carga dos valores padrão (uma tentativa)
  const fetchDefaultValues = useCallback(async () => {
    const cellValue = cell?.value;
    if (!cellValue || !token) return;

    const hasSuperUser = hasRole(
      "DOCUMENT_CREATION",
      "edit_custom_super_user",
      DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
    );

    const [profileParams, rulesObs, generationModeValue] = await Promise.all([
      readServiceCustomization(
        token,
        cellValue,
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
      readContestationCellRulesAsObservations(token, cellValue),
      readServiceCustomization(
        token,
        cellValue,
        CONTESTATION_SERVICE.ATTRIBUTES.GENERATION_MODE,
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      ),
    ]);

    // Aplicar tudo de uma vez
    if (profileParams) {
      setCustomizationParams((prev) => ({ ...prev, ...profileParams }));
    }

    if (
      rulesObs.client ||
      rulesObs.subsidios ||
      (hasSuperUser && rulesObs.internal)
    ) {
      setObservations({
        client: rulesObs.client || "",
        subsidios: rulesObs.subsidios || "",
        internal: hasSuperUser ? rulesObs.internal || "" : "",
      });
    }

    if (generationModeValue) {
      setGenerationMode(generationModeValue);
    }
  }, [cell?.value, token, hasRole]);

  // Função para carregar valores padrão da célula com retry automático
  const loadDefaultValues = useCallback(async () => {
    if (!cell?.value || !token || !hasCustomizationPermission()) return;

    cancelDefaultsRetryRef.current = false;
    setLoadingDefaults(true);
    setLoadDefaultsError(false);
    setDefaultsRetryCountdown(0);

    const attemptLoad = async () => {
      try {
        await fetchDefaultValues();
        setLoadDefaultsError(false);
        setLoadingDefaults(false);
      } catch (error) {
        console.error("Erro ao carregar valores padrão:", error);

        if (cancelDefaultsRetryRef.current) {
          setLoadingDefaults(false);
          return;
        }

        setLoadDefaultsError(true);

        for (let s = 3; s > 0; s--) {
          if (cancelDefaultsRetryRef.current) {
            setLoadingDefaults(false);
            return;
          }
          setDefaultsRetryCountdown(s);
          await new Promise((resolve) => {
            defaultsRetryTimerRef.current = setTimeout(resolve, 1000);
          });
        }

        if (!cancelDefaultsRetryRef.current) {
          setDefaultsRetryCountdown(0);
          setLoadDefaultsError(false);
          attemptLoad();
        } else {
          setLoadingDefaults(false);
        }
      }
    };

    attemptLoad();
  }, [cell?.value, token, fetchDefaultValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelDefaultsLoad = useCallback(() => {
    cancelDefaultsRetryRef.current = true;
    if (defaultsRetryTimerRef.current) {
      clearTimeout(defaultsRetryTimerRef.current);
      defaultsRetryTimerRef.current = null;
    }
    setLoadingDefaults(false);
    setLoadDefaultsError(false);
    setDefaultsRetryCountdown(0);
  }, []);

  const validateForm = useCallback(() => {
    const { inicialFile, subsidiosFiles } = fileData;
    const { lawyerName, lawyerOAB, local, date, empresaDefendida, jobName } =
      formData;

    const subsidiosTrim = (observations?.subsidios || "").trim();
    const canCustomizeParams =
      Boolean(user?.permissions) &&
      hasRole(
        "DOCUMENT_CREATION",
        "edit_custom_specific",
        DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
      );
    const needsSubsidiosRules =
      canCustomizeParams &&
      useDefaultParams &&
      subsidiosFiles &&
      subsidiosFiles.length > 0;

    const isValid =
      inicialFile !== null &&
      lawyerName.trim() !== "" &&
      lawyerOAB.trim() !== "" &&
      local.trim() !== "" &&
      date !== "" &&
      empresaDefendida.trim() !== "" &&
      jobName.trim() !== "" &&
      (!needsSubsidiosRules || subsidiosTrim !== "");

    setFormValid(isValid);
  }, [
    fileData,
    formData,
    useDefaultParams,
    observations,
    user,
    hasRole,
  ]);

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  useEffect(() => {
    let cancelled = false;
    let raw;
    try {
      raw = sessionStorage.getItem(REPLICATE_CONTESTACAO_STORAGE_KEY);
    } catch {
      raw = null;
    }
    if (!raw || !token) {
      return undefined;
    }

    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      try {
        sessionStorage.removeItem(REPLICATE_CONTESTACAO_STORAGE_KEY);
      } catch (_) {
        /* ignore */
      }
      return undefined;
    }

    if (
      !data?.files?.peticao_inicial?.token ||
      !isReplicatableContestacaoProductCode(data.product_code)
    ) {
      try {
        sessionStorage.removeItem(REPLICATE_CONTESTACAO_STORAGE_KEY);
      } catch (_) {
        /* ignore */
      }
      return undefined;
    }

    const blobToFile = (blob, filename) => {
      const name = filename || "arquivo";
      const ext = name.split(".").pop().toLowerCase();
      let mime = blob.type;
      if (!mime || mime === "application/octet-stream") {
        if (ext === "pdf") mime = "application/pdf";
        else if (ext === "png") mime = "image/png";
        else if (ext === "jpg" || ext === "jpeg") mime = "image/jpeg";
        else mime = "application/octet-stream";
      }
      return new File([blob], name, { type: mime });
    };

    const countPagesInEffect = async (file) => {
      if (!file || file.type !== "application/pdf") {
        return null;
      }
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        return pdf.numPages;
      } catch (e) {
        console.error(e);
        return null;
      }
    };

    const apply = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      if (cancelled) {
        return;
      }

      const snap = data.snapshot || {};
      const baseName = (data.job_name || "").trim();
      const suggestedName = baseName
        ? `${baseName} ${t("actions.replicateJobNameSuffix")}`
        : t("actions.replicateDefaultJobName");

      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({
        ...prev,
        lawyerName: snap.nome_advogado ?? "",
        lawyerOAB: snap.oab_advogado ?? "",
        local: snap.local ?? "",
        date: snap.data_documento || today,
        empresaDefendida: snap.empresa_defendida ?? "",
        jobName: suggestedName,
        observacoesGerais: snap.instrucoes_especificas ?? "",
      }));

      setObservations({
        client: snap.regras != null ? String(snap.regras) : "",
        internal:
          snap.regras_internas != null ? String(snap.regras_internas) : "",
        subsidios:
          snap.regras_subsidios != null ? String(snap.regras_subsidios) : "",
      });

      const hasCustom =
        (snap.custom_profile && typeof snap.custom_profile === "object") ||
        (snap.regras != null && String(snap.regras).trim() !== "") ||
        (snap.regras_internas != null &&
          String(snap.regras_internas).trim() !== "") ||
        (snap.regras_subsidios != null &&
          String(snap.regras_subsidios).trim() !== "");

      if (hasCustom) {
        setUseDefaultParams(true);
      }
      if (snap.custom_profile && typeof snap.custom_profile === "object") {
        setCustomizationParams((prev) => ({ ...prev, ...snap.custom_profile }));
        setOverriddenParams({
          creativity: true,
          tone: true,
          scope_topics: true,
          complexity: true,
          foundation_depth: true,
        });
      }

      if (GENERATION_MODES.includes(snap.modo_geracao)) {
        setGenerationMode(snap.modo_geracao);
      }

      let petFile = null;
      const subFiles = [];
      let anyFileError = false;

      try {
        const petBlob = await fetchContestacaoInputFileBlob(
          data.files.peticao_inicial.token,
          token,
        );
        if (cancelled) {
          return;
        }
        petFile = blobToFile(
          petBlob,
          data.files.peticao_inicial.filename || "peticao.pdf",
        );
      } catch (e) {
        console.error("Réplica contestação: petição inicial", e);
        anyFileError = true;
      }

      if (petFile && !cancelled) {
        for (const sm of data.files.subsidios || []) {
          if (!sm?.token) {
            continue;
          }
          try {
            const b = await fetchContestacaoInputFileBlob(sm.token, token);
            if (cancelled) {
              return;
            }
            subFiles.push(blobToFile(b, sm.filename || "subsidio.pdf"));
          } catch (e) {
            console.error("Réplica contestação: subsídio", e);
            anyFileError = true;
          }
        }
      }

      let pagesMap = {};

      if (petFile && !cancelled) {
        const pdfs = [petFile, ...subFiles].filter(
          (f) => f.type === "application/pdf",
        );
        for (const f of pdfs) {
          const n = await countPagesInEffect(f);
          if (cancelled) {
            return;
          }
          if (n != null) {
            pagesMap[f.name] = n;
          }
        }
      }

      if (!cancelled) {
        if (petFile) {
          setFileData({ inicialFile: petFile, subsidiosFiles: subFiles });
          setFilePages((prev) => ({ ...prev, ...pagesMap }));
          if (anyFileError) {
            setReplicateLoadError(t("actions.recreateContestacaoFilesError"));
          } else {
            setReplicateLoadError(null);
          }
        } else {
          setFileData((prev) => ({
            ...prev,
            inicialFile: null,
            subsidiosFiles: [],
          }));
          setReplicateLoadError(t("actions.recreateContestacaoFilesError"));
        }
        try {
          sessionStorage.removeItem(REPLICATE_CONTESTACAO_STORAGE_KEY);
        } catch (_) {
          /* ignore */
        }
      }
    };

    apply();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Carregar valores padrão quando o componente for montado
  useEffect(() => {
    let skipDefaults = false;
    try {
      skipDefaults = Boolean(
        sessionStorage.getItem(REPLICATE_CONTESTACAO_STORAGE_KEY),
      );
    } catch (_) {
      skipDefaults = false;
    }
    if (!skipDefaults) {
      loadDefaultValues();
    }
    return () => {
      cancelDefaultsRetryRef.current = true;
      if (defaultsRetryTimerRef.current) {
        clearTimeout(defaultsRetryTimerRef.current);
      }
    };
  }, [cell?.value, token, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Função para contar páginas de um PDF
  const countPdfPages = async (file) => {
    if (!file || file.type !== "application/pdf") {
      return null;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      return pdf.numPages;
    } catch (error) {
      console.error("Erro ao contar páginas do PDF:", error);
      return null;
    }
  };

  // Função para calcular o total de páginas
  const calculateTotalPages = () => {
    let total = 0;

    // Páginas do arquivo inicial
    if (fileData.inicialFile && filePages[fileData.inicialFile.name]) {
      total += filePages[fileData.inicialFile.name];
    }

    // Páginas dos arquivos de subsídios
    fileData.subsidiosFiles.forEach((file) => {
      if (filePages[file.name]) {
        total += filePages[file.name];
      }
    });

    return total;
  };

  // Sincronizar exibição de arquivos com o estado
  useEffect(() => {
    const filesContainer = document.getElementById("subsidios-list");
    if (!filesContainer) return;

    // Limpar container existente
    filesContainer.innerHTML = "";

    // Adicionar todos os arquivos do estado
    fileData.subsidiosFiles.forEach((file) => {
      const fileChip = document.createElement("div");
      fileChip.className = "file-chip";
      const pageCount = filePages[file.name];
      const pagesText =
        pageCount === 1
          ? t("contestacao.fileUpload.page")
          : t("contestacao.fileUpload.pages");

      // Criar estrutura separada para nome e páginas
      const nameSpan = document.createElement("span");
      nameSpan.className = "file-chip-name";
      nameSpan.textContent = file.name;

      fileChip.appendChild(nameSpan);

      if (pageCount) {
        const pagesSpan = document.createElement("span");
        pagesSpan.className = "file-chip-pages text-success";
        pagesSpan.textContent = ` (${pageCount} ${pagesText})`;
        fileChip.appendChild(pagesSpan);
      }

      const removeSpan = document.createElement("span");
      removeSpan.className = "remove";
      removeSpan.textContent = "×";
      fileChip.appendChild(removeSpan);

      filesContainer.appendChild(fileChip);

      // Adicionar handler de remoção
      const removeButton = fileChip.querySelector(".remove");
      if (removeButton) {
        removeButton.addEventListener("click", function (e) {
          if (!e) {
            console.warn("Event is null in file remove click handler");
            return;
          }

          try {
            fileChip.remove();
            const remaining = Array.from(
              document.querySelectorAll(".file-chip"),
            )
              .map((chip) => {
                // Extrair apenas o texto, removendo tags HTML e o botão de remover
                const text = chip.textContent.replace("×", "").trim();
                // Remover contagem de páginas do nome para comparação (com ou sem texto de páginas)
                // A regex remove padrões como "(9 pages)", "(9 páginas)", etc.
                return text
                  .replace(/\s*\(\d+\s+(página|páginas|page|pages)\)$/i, "")
                  .trim();
              })
              .filter((name) => name);

            setFileData((prev) => {
              const removedFiles = prev.subsidiosFiles.filter(
                (f) => !remaining.includes(f.name),
              );

              // Remover contagem de páginas dos arquivos removidos
              setFilePages((prevPages) => {
                const updated = { ...prevPages };
                removedFiles.forEach((f) => {
                  delete updated[f.name];
                });
                return updated;
              });

              return {
                ...prev,
                subsidiosFiles: prev.subsidiosFiles.filter((f) =>
                  remaining.includes(f.name),
                ),
              };
            });
          } catch (error) {
            console.error("Erro ao remover arquivo:", error);
          }
        });
      }
    });
  }, [fileData.subsidiosFiles, filePages, t]);

  const handleFileChange = async (e) => {
    // Verificação de segurança para evitar erro quando e.target é null
    if (!e || !e.target) {
      console.warn("Event target is null in handleFileChange");
      return;
    }

    const { id, files } = e.target;

    if (id === "subsidios-files") {
      // Adicionar novos arquivos à lista existente, evitando duplicatas
      const newFiles = Array.from(files);
      setFileData((prev) => {
        const existingFileNames = prev.subsidiosFiles.map((f) => f.name);
        const uniqueNewFiles = newFiles.filter(
          (file) => !existingFileNames.includes(file.name),
        );
        return {
          ...prev,
          subsidiosFiles: [...prev.subsidiosFiles, ...uniqueNewFiles],
        };
      });

      for (const file of newFiles) {
        if (file.type === "application/pdf") {
          const pageCount = await countPdfPages(file);
          if (pageCount !== null) {
            setFilePages((prev) => ({
              ...prev,
              [file.name]: pageCount,
            }));
          }
        }
      }

      // Limpar o input para permitir selecionar o mesmo arquivo novamente se necessário
      e.target.value = "";
    } else if (id === "inicial-file") {
      const selectedFile = files.length > 0 ? files[0] : null;
      setFileData((prev) => ({
        ...prev,
        inicialFile: selectedFile,
      }));

      if (selectedFile && selectedFile.type === "application/pdf") {
        const pageCount = await countPdfPages(selectedFile);
        if (pageCount !== null) {
          setFilePages((prev) => ({
            ...prev,
            [selectedFile.name]: pageCount,
          }));
        }
      }

      console.log(`Arquivo ${id} selecionado:`, selectedFile?.name);
    }
  };

  const handleInputChange = (e) => {
    // Verificação de segurança para evitar erro quando e.target é null
    if (!e || !e.target) {
      console.warn("Event target is null in handleInputChange");
      return;
    }

    const { id, value } = e.target;

    // Para sliders de personalização, converter para número
    const isPersonalizationSlider = [
      "criativity",
      "tone",
      "scope_topics",
      "complexity",
      "foundation_depth",
    ].includes(id);
    const finalValue = isPersonalizationSlider ? parseInt(value) : value;

    setFormData((prev) => ({
      ...prev,
      [id]: finalValue,
    }));
  };

  const handleProcessClick = async () => {
    // Validar os dados novamente antes de processar
    if (!formValid) return;

    // Verificar se o token está presente
    if (!token) {
      setError(t("common:authTokenNotFound"));
      return;
    }

    setError(null);
    setIsProcessing(true);

    try {
      // Preparar os dados para a API usando FormData
      const apiFormData = new FormData();

      // Adicionar os arquivos
      apiFormData.append("peticao_inicial", fileData.inicialFile);

      // Adicionar os subsídios (podem ser múltiplos arquivos)
      fileData.subsidiosFiles.forEach((file) => {
        apiFormData.append("subsidios", file);
      });

      // Adicionar os dados do advogado (usar o estado formData)
      apiFormData.append("nome_advogado", formData.lawyerName);
      apiFormData.append("oab_advogado", formData.lawyerOAB);
      apiFormData.append("local", formData.local);
      apiFormData.append("data_documento", formData.date);
      apiFormData.append("empresa_defendida", formData.empresaDefendida);
      apiFormData.append("job_name", formData.jobName);

      // Adicionar instruções específicas do caso se houver (sempre visível)
      if (formData.observacoesGerais) {
        apiFormData.append(
          "instrucoes_especificas",
          formData.observacoesGerais,
        );
      }

      // Adicionar parâmetros de personalização se estiver marcado para sobrescrever
      // ou se algum parâmetro foi sobrescrito individualmente
      const hasOverriddenParams = Object.values(overriddenParams).some(
        (overridden) => overridden,
      );

      if (useDefaultParams || hasOverriddenParams) {
        // Criar objeto CustomProfile com os parâmetros de personalização
        // Se useDefaultParams está marcado mas há parâmetros sobrescritos,
        // usar valores padrão da célula e substituir apenas os sobrescritos
        let customProfile = { ...customizationParams };

        if (useDefaultParams && hasOverriddenParams) {
          // Carregar valores padrão e substituir apenas os sobrescritos
          // Os valores padrão já estão em customizationParams após loadDefaultValues
          // Apenas garantir que valores não sobrescritos sejam None para o backend usar padrão
          customProfile = {};
          Object.keys(overriddenParams).forEach((key) => {
            if (overriddenParams[key]) {
              customProfile[key] = customizationParams[key];
            }
          });
        }

        // Adicionar como JSON string apenas se houver valores
        if (Object.keys(customProfile).length > 0) {
          apiFormData.append("custom_profile", JSON.stringify(customProfile));
        }

        // Adicionar regras (só quando marcado para sobrescrever parâmetros padrões)
        if (useDefaultParams) {
          if (safeObservations.client) {
            apiFormData.append("regras", safeObservations.client);
          }
          if (
            safeObservations.internal &&
            hasRole(
              "DOCUMENT_CREATION",
              "edit_custom_super_user",
              DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
            )
          ) {
            apiFormData.append("regras_internas", safeObservations.internal);
          }

          /* Sempre envia regras_subsidios quando sobrescreve, para o backend não
             recarregar o padrão da célula por ausência do campo (Form None). */
          apiFormData.append(
            "regras_subsidios",
            safeObservations.subsidios || ""
          );
        }
      }

      // Adicionar modo de geração (apenas se o usuário tem permissão para override)
      if (hasCustomizationPermission()) {
        apiFormData.append("modo_geracao", generationMode);
      }

      // URL da API
      const cellId = cell?.value || "undefined";
      const url = `${API_BASE_URL}/doc_creator/cria_contestacao?cell_id=${cellId}`;

      // Log para diagnóstico
      console.log(`Cell value na criação:`, cell?.value);
      console.log(`Enviando solicitação para: ${url}`);

      // Fazer a chamada para iniciar o processamento
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${token}`,
          ServiceName: "DOCUMENT_CREATION",
        },
        body: apiFormData,
      });

      if (!response.ok) {
        // Tentar obter mais detalhes do erro
        let errorMessage = `${response.status} ${response.statusText}`;
        try {
          const errorData = await response.json();
          // Extrair a mensagem de erro da API (campo 'detail' ou 'message')
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (typeof errorData === "string") {
            errorMessage = errorData;
          }
        } catch (parseError) {
          // Se não conseguir parsear o JSON, usar a mensagem de status HTTP
          console.warn(
            "Não foi possível parsear resposta de erro:",
            parseError,
          );
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (!data.job_id) {
        throw new Error(t("common:invalidResponse"));
      }

      console.log(`Job iniciado com sucesso! Job ID: ${data.job_id}`);

      // Redirecionar para a lista de jobs
      history.push("/contestacoes");
    } catch (error) {
      console.error("Erro ao processar documento:", error);
      setError(t("common:processingError", { error: error.message }));
      setIsProcessing(false);
    }
  };

  return (
    <div className="container contestacao-page">
      <header>
        <div className="flex-row-between mb-20">
          <div>
            <h1>{t("contestacaoCreate.title")}</h1>
          </div>

          <button
            onClick={() => history.push("/contestacoes")}
            className="ikaro-btn ikaro-btn-primary ikaro-btn-md"
          >
            {t("contestacaoCreate.backToList")}
          </button>
        </div>
      </header>

      {replicateLoadError && (
        <Alert
          severity="warning"
          className="mb-20"
          onClose={() => setReplicateLoadError(null)}
        >
          {replicateLoadError}
        </Alert>
      )}

      <div className="card">
        <h3 className="card-title">
          <i>📝</i> {t("contestacaoCreate.taskCardTitle")}
        </h3>

        <div className="form-group">
          <label htmlFor="jobName" className="required">
            {t("contestacaoCreate.taskNameLabel")}
          </label>
          <input
            type="text"
            id="jobName"
            placeholder={t("contestacaoCreate.taskNamePlaceholder")}
            required
            value={formData.jobName}
            onChange={handleInputChange}
          />
          <div className="info-text">{t("contestacaoCreate.taskNameHint")}</div>
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>📄</i> {t("contestacaoCreate.requiredFiles")}
        </h3>

        <div className="form-group">
          <div className="file-input">
            <label className="required">
              {t("contestacaoCreate.initialPetition")}
            </label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.inicialFile ? (
                    (() => {
                      const pageCount = filePages[fileData.inicialFile.name];
                      if (pageCount) {
                        const pagesText =
                          pageCount === 1
                            ? t("contestacao.fileUpload.page")
                            : t("contestacao.fileUpload.pages");
                        return (
                          <>
                            <span className="file-name-text">
                              {fileData.inicialFile.name}
                            </span>
                            <span className="file-name-pages text-success">
                              ({pageCount} {pagesText})
                            </span>
                          </>
                        );
                      }
                      return (
                        <span className="file-name-text">
                          {fileData.inicialFile.name}
                        </span>
                      );
                    })()
                  ) : (
                    <span className="file-name-text">
                      {t("contestacaoCreate.selectFile")}
                    </span>
                  )}
                </span>
              </div>
              <input
                type="file"
                id="inicial-file"
                accept=".pdf,.jpg,.jpeg,.png"
                required
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="info-text">
            {t("contestacaoCreate.initialPetitionInfo")}
          </div>
        </div>

        <div className="form-group">
          <div className="file-input">
            <label className="flex-row gap-8">
              {t("contestacaoCreate.subsidies")}
              <span className="tooltip-wrapper">
                <Info className="tooltip-icon" />
                <span className="tooltip-content">
                  {t("contestacaoCreate.subsidiesInfo")}
                </span>
              </span>
            </label>
            <div className="file-upload">
              <div className="file-upload-label">
                <i>📎</i>
                <span className="file-name">
                  {fileData.subsidiosFiles.length > 0
                    ? `${fileData.subsidiosFiles.length} ${t(
                        "contestacaoCreate.filesSelected",
                      )}`
                    : t("contestacaoCreate.selectFiles")}
                </span>
              </div>
              <input
                type="file"
                id="subsidios-files"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp3,.wav,.m4a,.aac,.ogg,.oga,.opus,.webm,.flac,.wma,.amr,audio/*"
                onChange={handleFileChange}
              />
            </div>
          </div>
          <div className="multiple-files" id="subsidios-list"></div>
          {/* Total de páginas informativo - centralizado */}
          {(() => {
            const totalPages = calculateTotalPages();
            if (totalPages > 0) {
              const pagesText =
                totalPages === 1
                  ? t("contestacao.fileUpload.page")
                  : t("contestacao.fileUpload.pages");
              return (
                <div className="info-text mt-8 text-center font-medium text-success">
                  {t("contestacao.fileUpload.totalPages", {
                    total: totalPages,
                    pagesText,
                  })}
                </div>
              );
            }
            return null;
          })()}
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>🏢</i> {t("contestacaoCreate.companyInfo")}
        </h3>

        <div className="form-group">
          <label htmlFor="empresaDefendida" className="required">
            {t("contestacaoCreate.defendedCompany")}
          </label>
          <input
            type="text"
            id="empresaDefendida"
            placeholder={t("contestacaoCreate.defendedCompanyPlaceholder")}
            required
            value={formData.empresaDefendida}
            onChange={handleInputChange}
          />
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">
          <i>✏️</i> {t("contestacaoCreate.lawyerInfo")}
        </h3>

        <div className="form-group">
          <label htmlFor="lawyerName" className="required">
            {t("contestacaoCreate.fullName")}
          </label>
          <input
            type="text"
            id="lawyerName"
            placeholder={t("contestacaoCreate.fullNamePlaceholder")}
            required
            value={formData.lawyerName}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="lawyerOAB" className="required">
            {t("contestacaoCreate.oabNumber")}
          </label>
          <input
            type="text"
            id="lawyerOAB"
            placeholder={t("contestacaoCreate.oabNumberPlaceholder")}
            required
            value={formData.lawyerOAB}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="local" className="required">
            {t("contestacaoCreate.location")}
          </label>
          <input
            type="text"
            id="local"
            placeholder={t("contestacaoCreate.locationPlaceholder")}
            required
            value={formData.local}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label htmlFor="date" className="required">
            {t("contestacaoCreate.contestationDate")}
          </label>
          <input
            type="date"
            id="date"
            required
            value={formData.date}
            onChange={handleInputChange}
          />
        </div>
      </div>

      {/* Campo de instruções específicas - sempre visível para todos */}
      <div className="card">
        <h3 className="card-title">
          <i>⚙️</i> Regras
        </h3>

        <div className="form-group">
          <label htmlFor="observacoesGerais">
            {t("contestacaoCreate.parameters.specificInstructions")}
          </label>
          <textarea
            id="observacoesGerais"
            placeholder={t(
              "contestacaoCreate.parameters.specificInstructionsPlaceholder",
            )}
            value={formData.observacoesGerais}
            onChange={handleInputChange}
            rows="4"
          />
          <div className="info-text">
            {t("contestacaoCreate.parameters.specificInstructionsInfo")}
          </div>
        </div>
      </div>

      {/* Seção de parâmetros avançados - só aparece para usuários com permissão */}
      {hasCustomizationPermission() && (
        <div className="card" style={{ position: "relative" }}>
          <h3 className="card-title">
            <i>⚙️</i> Parâmetros Avançados
          </h3>

          {loadingDefaults && (
            <div
              className="cell-loading-overlay"
              style={{ borderRadius: "8px" }}
            >
              <div className="cell-loading-content">
                <div className="cell-loading-spinner" />
                {loadDefaultsError ? (
                  <>
                    <Typography className="cell-loading-retry-text">
                      {t("contestacao.cellParams.loading.retrying", {
                        seconds: defaultsRetryCountdown,
                      })}
                    </Typography>
                    <button
                      type="button"
                      className="cell-loading-cancel-btn"
                      onClick={handleCancelDefaultsLoad}
                    >
                      {t("contestacao.cellParams.loading.cancel")}
                    </button>
                  </>
                ) : (
                  <Typography className="cell-loading-text">
                    {t("contestacao.cellParams.loading.loadingData")}
                  </Typography>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={useDefaultParams}
                onChange={handleUseDefaultParamsChange}
              />
              <span className="checkmark"></span>
              {t("contestacaoCreate.parameters.useDefaultParams")}
            </label>
            <p className="info-text">
              {t("contestacaoCreate.parameters.useDefaultParamsDescription")}
            </p>

            {/* Regras customizadas: logo abaixo do checkbox de sobrescrever */}
            {useDefaultParams && (
              <div className="expanded-params">
                <div className="custom-rules-section">
                  <div className="form-group">
                    <label htmlFor="client-custom-rules">
                      {t("contestacao.cellParams.customRules.client")}
                    </label>
                    <div className="relative">
                      <textarea
                        id="client-custom-rules"
                        placeholder={t(
                          "contestacao.cellParams.customRules.clientPlaceholder",
                        )}
                        value={safeObservations.client}
                        onChange={handleClientObservationChange}
                        rows="4"
                      />
                      <button
                        type="button"
                        onClick={() => handleExpandTextarea("client")}
                        className="contestacao-fullscreen-expand-btn"
                        title="Expandir em tela cheia"
                      >
                        <Fullscreen className="contestacao-expand-icon" />
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="subsidios-custom-rules">
                      {t("contestacaoCreate.parameters.subsidiosRules")}
                    </label>
                    <div className="relative">
                      <textarea
                        id="subsidios-custom-rules"
                        placeholder={t(
                          "contestacaoCreate.parameters.subsidiosRulesPlaceholder"
                        )}
                        value={safeObservations.subsidios}
                        onChange={handleSubsidiosObservationChange}
                        rows="4"
                      />
                      <button
                        type="button"
                        onClick={() => handleExpandTextarea("subsidios")}
                        className="contestacao-fullscreen-expand-btn"
                        title="Expandir em tela cheia"
                      >
                        <Fullscreen className="contestacao-expand-icon" />
                      </button>
                    </div>
                    <div className="info-text">
                      {fileData.subsidiosFiles.length > 0
                        ? t(
                            "contestacaoCreate.parameters.subsidiosRulesInfoWithFiles"
                          )
                        : safeObservations.subsidios
                          ? t(
                              "contestacaoCreate.parameters.subsidiosRulesInfoOverride",
                            )
                          : t(
                              "contestacaoCreate.parameters.subsidiosRulesInfoDefault",
                            )}
                    </div>
                  </div>

                  {hasRole(
                    "DOCUMENT_CREATION",
                    "edit_custom_super_user",
                    DOCUMENT_CREATION_PRODUCT_CODES.CONTESTACAO,
                  ) && (
                    <div className="form-group">
                      <label htmlFor="internal-custom-rules">
                        {t("contestacao.cellParams.customRules.internal")}
                      </label>
                      <div className="relative">
                        <textarea
                          id="internal-custom-rules"
                          placeholder={t(
                            "contestacao.cellParams.customRules.internalPlaceholder",
                          )}
                          value={safeObservations.internal}
                          onChange={handleInternalObservationChange}
                          rows="4"
                        />
                        <button
                          type="button"
                          onClick={() => handleExpandTextarea("internal")}
                          className="contestacao-fullscreen-expand-btn"
                          title="Expandir em tela cheia"
                        >
                          <Fullscreen className="contestacao-expand-icon" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Checkbox para sobrescrever parâmetros individuais */}
          <div className="form-group">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={Object.values(overriddenParams).some((o) => o)}
                onChange={(e) => {
                  if (!e.target.checked) {
                    // Se desmarcar, limpar todas as sobrescritas
                    setOverriddenParams({
                      creativity: false,
                      tone: false,
                      scope_topics: false,
                      complexity: false,
                      foundation_depth: false,
                    });
                  } else {
                    // Se marcar, ativar pelo menos um parâmetro para mostrar os sliders
                    // Por padrão, ativa o primeiro parâmetro (tone)
                    setOverriddenParams({
                      creativity: false,
                      tone: true,
                      scope_topics: false,
                      complexity: false,
                      foundation_depth: false,
                    });
                  }
                }}
              />
              <span className="checkmark"></span>
              {t("contestacaoCreate.parameters.overrideIndividualParams")}
            </label>
            <p className="info-text">
              {t(
                "contestacaoCreate.parameters.overrideIndividualParamsDescription",
              )}
            </p>

            {/* Sliders aparecem quando o checkbox está marcado */}
            {Object.values(overriddenParams).some((o) => o) && (
              <div className="expanded-params mt-20 p-16">
                <CustomizationSliders
                  initialValues={customizationParams}
                  onChange={(newParams) => {
                    setCustomizationParams(newParams);
                    // Marcar como sobrescrito quando o usuário alterar
                    const newOverridden = { ...overriddenParams };
                    Object.keys(newParams).forEach((key) => {
                      if (
                        newParams[key] !== customizationParams[key] ||
                        overriddenParams[key]
                      ) {
                        newOverridden[key] = true;
                      }
                    });
                    setOverriddenParams(newOverridden);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {hasCustomizationPermission() && (
        <div className="card">
          <h3 className="card-title">
            <i>🚀</i> {t("contestacao.cellParams.generationMode.title")}
          </h3>
          <div className="form-group">
            <GenerationModeSelector
              value={generationMode}
              onChange={(mode) => setGenerationMode(mode)}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="error-alert">
          <i className="error-icon">⚠️</i>
          <span>{error}</span>
        </div>
      )}

      <button
        id="process-btn"
        className="process-btn"
        disabled={!formValid || isProcessing}
        onClick={handleProcessClick}
      >
        {isProcessing
          ? t("contestacaoCreate.processing")
          : t("contestacaoCreate.generateContestation")}
      </button>

      <footer>
        <p>{t("contestacaoCreate.footer")}</p>
      </footer>

      {/* Modal fullscreen para textarea expandido */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        fullScreen
        PaperProps={{
          className: "contestacao-fullscreen-paper",
        }}
      >
        <DialogTitle className="contestacao-dialog-title">
          <Typography variant="h6">
            {fullscreenField === "client" &&
              t("contestacao.cellParams.customRules.client")}
            {fullscreenField === "internal" &&
              t("contestacao.cellParams.customRules.internal")}
            {fullscreenField === "subsidios" &&
              t("contestacaoCreate.parameters.subsidiosRulesModalTitle")}
          </Typography>
          <Button
            onClick={handleCloseFullscreen}
            size="small"
            className="contestacao-dialog-close-btn"
          >
            <FullscreenExit />
          </Button>
        </DialogTitle>
        <DialogContent className="contestacao-dialog-content">
          <TextField
            fullWidth
            multiline
            value={fullscreenValue}
            onChange={(e) => setFullscreenValue(e.target.value)}
            variant="outlined"
            placeholder={
              fullscreenField === "client"
                ? t("contestacao.cellParams.customRules.clientPlaceholder")
                : fullscreenField === "internal"
                  ? t("contestacao.cellParams.customRules.internalPlaceholder")
                  : t(
                      "contestacaoCreate.parameters.subsidiosRulesPlaceholder"
                    )
            }
            InputProps={{
              className: "contestacao-fullscreen-input",
            }}
            inputProps={{
              className: "contestacao-fullscreen-input-inner",
            }}
            className="flex-1"
          />
        </DialogContent>
        <DialogActions className="contestacao-dialog-actions">
          <Button
            onClick={handleCloseFullscreen}
            variant="contained"
            className="ikaro-btn-primary"
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Contestacao;
